import React from 'react';

/*
 * Small, dependency-free markdown renderer for widget answers (headings, lists, tables, code,
 * links, images, bold/italic/inline code). Builds React elements only - no innerHTML - so model
 * output can never inject markup into the host page. Styled by `.aaw-md` in AegisAssistWidget.css.
 */

const SAFE_LINK = /^(https?:|mailto:|tel:)/i;
const INLINE_RE = /(!\[([^\]]*)\]\(([^)\s]+)\))|(\[([^\]]+)\]\(([^)\s]+)\))|(`([^`]+)`)|(\*\*([^*]+)\*\*)|(\*([^*\n]+)\*)|(_([^_\n]+)_)/g;

function renderInline(text, key, resolveImageUrl) {
    const out = [];
    let last = 0;
    let match;
    // Fresh regex per call: renderInline recurses (bold), and a shared global regex's lastIndex would loop forever.
    const re = new RegExp(INLINE_RE.source, 'g');
    while ((match = re.exec(text)) !== null) {
        if (match.index > last) out.push(text.slice(last, match.index));
        const k = `${key}-${match.index}`;
        if (match[1]) {
            const src = resolveImageUrl ? resolveImageUrl(match[3]) : match[3];
            if (src && (SAFE_LINK.test(src) || src.startsWith('/') || src.startsWith('blob:'))) {
                out.push(
                    <a key={k} className="aaw-md-img" href={src} target="_blank" rel="noopener noreferrer">
                        <img src={src} alt={match[2] || 'Image'} loading="lazy" />
                        {match[2] ? <span>{match[2]}</span> : null}
                    </a>,
                );
            }
        } else if (match[4]) {
            const href = match[6];
            out.push(SAFE_LINK.test(href)
                ? <a key={k} href={href} target="_blank" rel="noopener noreferrer">{match[5]}</a>
                : match[5]);
        } else if (match[7]) {
            out.push(<code key={k}>{match[8]}</code>);
        } else if (match[9]) {
            out.push(<strong key={k}>{renderInline(match[10], k, resolveImageUrl)}</strong>);
        } else if (match[11] || match[13]) {
            out.push(<em key={k}>{match[12] || match[14]}</em>);
        }
        last = match.index + match[0].length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
}

const isTableRow = (line) => /^\s*\|.*\|\s*$/.test(line);
const isTableDivider = (line) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(line);
const splitRow = (line) => line.trim().replace(/^\||\|$/g, '').split('|').map((cell) => cell.trim());

function parseBlocks(content) {
    const lines = String(content || '').replace(/\r\n/g, '\n').split('\n');
    const blocks = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];

        if (!line.trim()) {
            i += 1;
            continue;
        }

        if (line.trim().startsWith('```')) {
            const code = [];
            i += 1;
            while (i < lines.length && !lines[i].trim().startsWith('```')) {
                code.push(lines[i]);
                i += 1;
            }
            i += 1;
            blocks.push({ type: 'code', text: code.join('\n') });
            continue;
        }

        const heading = line.match(/^(#{1,4})\s+(.*)$/);
        if (heading) {
            blocks.push({ type: 'heading', level: heading[1].length, text: heading[2] });
            i += 1;
            continue;
        }

        if (isTableRow(line) && i + 1 < lines.length && isTableDivider(lines[i + 1])) {
            const header = splitRow(line);
            const rows = [];
            i += 2;
            while (i < lines.length && isTableRow(lines[i])) {
                rows.push(splitRow(lines[i]));
                i += 1;
            }
            blocks.push({ type: 'table', header, rows });
            continue;
        }

        const listMatch = line.match(/^\s*([-*•]|\d+[.)])\s+(.*)$/);
        if (listMatch) {
            const ordered = /\d/.test(listMatch[1]);
            const items = [];
            while (i < lines.length) {
                const item = lines[i].match(/^\s*([-*•]|\d+[.)])\s+(.*)$/);
                if (!item || /\d/.test(item[1]) !== ordered) break;
                items.push(item[2]);
                i += 1;
            }
            blocks.push({ type: ordered ? 'ol' : 'ul', items });
            continue;
        }

        const para = [];
        while (
            i < lines.length &&
            lines[i].trim() &&
            !lines[i].trim().startsWith('```') &&
            !/^(#{1,4})\s+/.test(lines[i]) &&
            !/^\s*([-*•]|\d+[.)])\s+/.test(lines[i]) &&
            !(isTableRow(lines[i]) && i + 1 < lines.length && isTableDivider(lines[i + 1]))
        ) {
            para.push(lines[i]);
            i += 1;
        }
        blocks.push({ type: 'p', lines: para });
    }
    return blocks;
}

export default function WidgetMarkdown({ content, isStreaming, resolveImageUrl }) {
    const blocks = React.useMemo(() => parseBlocks(content), [content]);

    return (
        <div className={`aaw-md${isStreaming ? ' is-streaming' : ''}`}>
            {blocks.map((block, index) => {
                const key = `b${index}`;
                switch (block.type) {
                    case 'heading': {
                        const Tag = `h${Math.min(block.level + 2, 6)}`;
                        return <Tag key={key}>{renderInline(block.text, key, resolveImageUrl)}</Tag>;
                    }
                    case 'code':
                        return <pre key={key}><code>{block.text}</code></pre>;
                    case 'ul':
                    case 'ol': {
                        const Tag = block.type;
                        return (
                            <Tag key={key}>
                                {block.items.map((item, itemIndex) => (
                                    <li key={`${key}-${itemIndex}`}>{renderInline(item, `${key}-${itemIndex}`, resolveImageUrl)}</li>
                                ))}
                            </Tag>
                        );
                    }
                    case 'table':
                        return (
                            <div key={key} className="aaw-md-table">
                                <table>
                                    <thead>
                                        <tr>{block.header.map((cell, c) => <th key={c}>{renderInline(cell, `${key}-h${c}`, resolveImageUrl)}</th>)}</tr>
                                    </thead>
                                    <tbody>
                                        {block.rows.map((row, r) => (
                                            <tr key={r}>{row.map((cell, c) => <td key={c}>{renderInline(cell, `${key}-${r}-${c}`, resolveImageUrl)}</td>)}</tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        );
                    default:
                        return (
                            <p key={key}>
                                {block.lines.map((line, lineIndex) => (
                                    <React.Fragment key={lineIndex}>
                                        {lineIndex > 0 ? <br /> : null}
                                        {renderInline(line, `${key}-${lineIndex}`, resolveImageUrl)}
                                    </React.Fragment>
                                ))}
                            </p>
                        );
                }
            })}
        </div>
    );
}
